<template>
  <div class="contractDetail">
    <div class="add-content-container">
      <div class="detail-body" v-loading="loading">
        <div class="detail-section">
          <div class="section-title">合同信息</div>
          <div class="detail-grid">
            <div class="detail-row" v-for="item in infoItems" :key="item.label">
              <span class="detail-label">{{ item.label }}</span>
              <span class="detail-value">{{ item.value }}</span>
            </div>
            <div class="detail-row detail-row-full">
              <span class="detail-label">合同附件</span>
              <span class="detail-value">
                <template v-if="signedFiles.length">
                  <a
                    v-for="(file, index) in signedFiles"
                    :key="index"
                    :href="file.url"
                    class="file-link"
                    target="_blank"
                    rel="noopener"
                  >{{ file.name }}</a>
                </template>
                <span v-else>-</span>
              </span>
            </div>
          </div>
        </div>

        <div class="detail-section">
          <div class="section-title">关联标的物</div>
          <div class="table-wrap">
            <el-table :data="detail.properties || []" border size="small" style="width:100%" :header-cell-style="{ background: '#eee' }">
              <i slot="empty" style="font-style:normal">暂无数据</i>
              <el-table-column prop="propertyCode" label="标的物编号" align="center" min-width="140" />
              <el-table-column label="标的物类型" align="center" min-width="120">
                <template slot-scope="scope">{{ getPropertyTypeText(scope.row) }}</template>
              </el-table-column>
              <el-table-column prop="area" label="面积(亩)" align="center" min-width="100" />
              <el-table-column prop="address" label="地址" align="center" min-width="180" show-overflow-tooltip />
              <el-table-column prop="rentAmount" label="年租金(元)" align="center" min-width="120" />
            </el-table>
            <div class="summary-row">
              <div class="summary-cell">
                <span class="summary-label">标的物数量</span>
                <span class="summary-value">{{ propertyCount }}</span>
              </div>
              <div class="summary-cell">
                <span class="summary-label">标的物面积合计(亩)</span>
                <span class="summary-value">{{ formatMoney(totalArea) }}</span>
              </div>
              <div class="summary-cell">
                <span class="summary-label">租金合计(元)</span>
                <span class="summary-value">{{ formatMoney(totalRent) }}</span>
              </div>
            </div>
          </div>
        </div>

        <div class="detail-section">
          <div class="section-title section-title-between">
            <span>租金缴纳记录</span>
            <el-button
              v-if="edit && canPay"
              size="small"
              type="primary"
              @click="openPay"
            >线下代缴</el-button>
          </div>
          <div class="table-wrap">
            <div class="summary-row summary-row-top">
              <div class="summary-cell">
                <span class="summary-label">租金合计(元)</span>
                <span class="summary-value">{{ formatMoney(totalRent) }}</span>
              </div>
              <div class="summary-cell">
                <span class="summary-label">已缴金额</span>
                <span class="summary-value">{{ moneyWithYuan(detail.paidAmount) }}</span>
              </div>
              <div class="summary-cell">
                <span class="summary-label">剩余未缴</span>
                <span class="summary-value">{{ moneyWithYuan(detail.unpaidAmount) }}</span>
              </div>
            </div>
            <el-table :data="detail.rentRecords || []" border size="small" style="width:100%" :header-cell-style="{ background: '#eee' }">
              <i slot="empty" style="font-style:normal">暂无数据</i>
              <el-table-column prop="amountPaid" label="已缴金额(元)" align="center" min-width="130" />
              <el-table-column prop="unpaidAmount" label="剩余未缴(元)" align="center" min-width="130" />
              <el-table-column label="支付方式" align="center" min-width="100">
                <template slot-scope="scope">{{ enumLabel("payMethod", scope.row.payMethod) }}</template>
              </el-table-column>
              <el-table-column label="缴费时间" align="center" min-width="170">
                <template slot-scope="scope">{{ formatDateTime(scope.row.payDate) }}</template>
              </el-table-column>
              <el-table-column label="支付凭证" align="center" min-width="140">
                <template slot-scope="scope">
                  <template v-if="getVoucherUrls(scope.row).length">
                    <lease-auth-image
                      v-for="(url, idx) in getVoucherUrls(scope.row)"
                      :key="scope.row.id + '-' + idx"
                      class="voucher-thumb"
                      :src="url"
                      :width="40"
                      :height="40"
                    />
                  </template>
                  <span v-else>--</span>
                </template>
              </el-table-column>
              <el-table-column label="操作" align="center" min-width="100">
                <template slot-scope="scope">
                  <span
                    v-if="scope.row.voucherUrl"
                    class="content-edit"
                    @click="downloadVoucher(scope.row)"
                  >下载凭证</span>
                  <span v-else>--</span>
                </template>
              </el-table-column>
            </el-table>
          </div>
        </div>
      </div>
    </div>

    <offline-pay-dialog ref="offlinePay" @success="loadDetail" />
  </div>
</template>

<script>
import moment from "moment";
import mixins from "@/common/MixIn.js";
import OfflinePayDialog from "@/components/fishPondLease/common/OfflinePayDialog.vue";
import LeaseAuthImage from "@/components/fishPondLease/common/LeaseAuthImage.vue";
import {
  getContract,
  leaseRequestError,
  resolveLeaseResourceUrl,
  downloadBlobGet
} from "@/service/fishPondLease";
import {
  ContractStatus,
  enumLabel
} from "@/service/fishPondLeaseEnums";

export default {
  name: "ContractDetail",
  mixins: [mixins],
  components: { OfflinePayDialog, LeaseAuthImage },
  data() {
    return {
      loading: false,
      detail: {}
    };
  },
  computed: {
    contractId() {
      return (this.$route.query && this.$route.query.id) || null;
    },
    canPay() {
      const s = Number(this.detail.contractStatus);
      return s === ContractStatus.Signed || s === ContractStatus.Performing;
    },
    infoItems() {
      const d = this.detail || {};
      return [
        { label: "标的物", value: this.propertyTypeText },
        { label: "合同名称", value: d.contractName || "-" },
        { label: "合同开始日期", value: this.formatDate(d.contractStart) },
        { label: "合同类型", value: enumLabel("assetType", d.assetType) },
        { label: "租赁期限", value: this.formatLeaseTerm(d.leaseTerm) },
        { label: "承租方名称", value: d.tenantName || "-" },
        { label: "签约方式", value: enumLabel("signMethod", d.signMethod) },
        { label: "合同上传人", value: d.signUploader || "-" }
      ];
    },
    propertyList() {
      return this.detail.properties || [];
    },
    propertyTypeText() {
      const labels = this.propertyList
        .map(item => this.getPropertyTypeText(item))
        .filter(Boolean);
      return labels.length ? Array.from(new Set(labels)).join("、") : "鱼塘";
    },
    signedFiles() {
      if (!this.detail.signedFileUrl) return [];
      return [
        {
          name: "签约文件",
          url: this.resolveFileUrl(this.detail.signedFileUrl)
        }
      ];
    },
    propertyCount() {
      return this.propertyList.length;
    },
    totalArea() {
      return this.propertyList.reduce((sum, item) => sum + (Number(item.area) || 0), 0);
    },
    totalRent() {
      return this.propertyList.reduce((sum, item) => {
        return sum + (Number(item.rentAmount) || 0) * (Number(this.detail.leaseTerm) || 0);
      }, 0);
    }
  },
  async mounted() {
    await this.initPagePermission();
    this.loadDetail();
  },
  methods: {
    enumLabel,
    formatDate(val) {
      if (!val) return "-";
      return moment(val).format("YYYY-MM-DD");
    },
    formatDateTime(val) {
      if (!val) return "--";
      return moment(val).format("YYYY-MM-DD HH:mm:ss");
    },
    formatLeaseTerm(months) {
      if (!months) return "-";
      const y = months / 12;
      if (y === Math.floor(y)) return y + "年";
      return months + "月";
    },
    formatMoney(val) {
      if (val == null || val === "") return "--";
      return Number(val).toLocaleString("zh-CN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    },
    moneyWithYuan(val) {
      const money = this.formatMoney(val);
      return money === "--" ? "--" : `￥${money}`;
    },
    resolveFileUrl(url) {
      return resolveLeaseResourceUrl(url);
    },
    getPropertyTypeText(row) {
      if (!row) return "";
      const type = enumLabel("propertyType", row.type);
      const subType = enumLabel("propertySubType", row.subType);
      return subType && subType !== "-" ? `${type} - ${subType}` : type;
    },
    getVoucherUrls(row) {
      if (!row || !row.voucherUrl) return [];
      return String(row.voucherUrl)
        .split(",")
        .map(item => item && item.trim())
        .filter(Boolean);
    },
    loadDetail() {
      if (!this.contractId) {
        this.$message.warning("缺少合同Id");
        return;
      }
      this.loading = true;
      getContract(this.contractId)
        .then(res => {
          this.detail = (res && res.data) || {};
        })
        .catch(err => {
          this.$message.error(leaseRequestError(err));
        })
        .finally(() => {
          this.loading = false;
        });
    },
    openPay() {
      if (this.$refs.offlinePay) {
        this.$refs.offlinePay.open(this.contractId);
      }
    },
    downloadVoucher(row) {
      downloadBlobGet(`/api/FishPondLeaseRent/voucher/download/${row.id}`, "支付凭证");
    }
  }
};
</script>

<style scoped>
.detail-body {
  padding: 16px 24px 40px;
  background: #fff;
}
.detail-section + .detail-section {
  margin-top: 20px;
}
.section-title {
  margin: 0 0 12px;
  font-size: 15px;
  font-weight: 600;
  color: #333;
}
.section-title-between {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.detail-grid {
  display: flex;
  flex-wrap: wrap;
  margin: 0 -8px;
}
.detail-row {
  width: 33.3333%;
  padding: 0 8px;
  margin-bottom: 10px;
  box-sizing: border-box;
}
.detail-row-full {
  width: 100%;
}
.detail-label,
.detail-value {
  display: block;
  line-height: 22px;
  word-break: break-all;
}
.detail-label {
  color: #909399;
}
.detail-value {
  color: #303133;
  min-height: 22px;
}
.table-wrap {
  border: 1px solid #ebeef5;
  overflow: hidden;
  background: #fff;
}
.table-wrap >>> .el-table::before {
  height: 1px !important;
  background-color: #ebeef5 !important;
  border-bottom: none !important;
}
.summary-row {
  display: flex;
  background: #fafafa;
  border-top: 1px solid #ebeef5;
}
.summary-row-top {
  border-top: none;
  border-bottom: 1px solid #ebeef5;
}
.summary-cell {
  flex: 1;
  padding: 12px 16px;
}
.summary-label {
  color: #909399;
  margin-right: 8px;
}
.summary-value {
  color: #3389ff;
  font-weight: 600;
}
.file-link {
  display: block;
  color: #3389ff;
  text-decoration: none;
  line-height: 24px;
}
.file-link:hover {
  text-decoration: underline;
}
.voucher-thumb {
  margin-right: 4px;
  border-radius: 4px;
  overflow: hidden;
  vertical-align: middle;
}
.content-edit {
  color: #3389ff;
  cursor: pointer;
}
@media screen and (max-width: 1200px) {
  .detail-row {
    width: 50%;
  }
}
@media screen and (max-width: 768px) {
  .detail-row {
    width: 100%;
  }
  .summary-row {
    flex-direction: column;
  }
  .summary-cell {
    border-right: none;
    border-bottom: 1px solid #ebeef5;
  }
  .summary-cell:last-child {
    border-bottom: none;
  }
}
</style>
